import apiClient from "./client";
import { appAbsoluteUrl } from "@/utils/url";

/**
 * Calls that provision a tenant in-request (schema, every tenant migration,
 * seed data) outlast the client's 30 s default - a trial took ~36 s in
 * production. Wait well past that; the pages poll the signup status alongside
 * and keep waiting even if this runs out.
 */
export const SIGNUP_PROVISIONING_TIMEOUT_MS = 180_000;

/**
 * @typedef {import('@/types/models/user').User} User
 */

/**
 * Authentication API module
 * Returns raw JSON:API responses - normalization happens in Redux thunks
 */
const authAPI = {
  /**
   * Login with email and password
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise<Object>} JSON:API response with user data and token
   */
  login: async (email, password, tenantSubdomain, rememberMe = false) => {
    const payload = { email, password };
    if (tenantSubdomain) {
      payload.tenant_subdomain = tenantSubdomain;
    }
    payload.remember_me = !!rememberMe;
    const response = await apiClient.post("/auth/login/", payload);
    return response.data;
  },

  /**
   * Resolve tenant login context and available auth methods.
   * @param {Object} params
   * @param {string} [params.tenant_subdomain]
   * @param {number|string} [params.tenant_id]
   * @returns {Promise<Object>}
   */
  getLoginContext: async (params = {}) => {
    const response = await apiClient.get("/auth/login-context/", { params });
    return response.data;
  },

  /**
   * Get current authenticated user
   * @returns {Promise<Object>} JSON:API response with user data
   */
  getCurrentUser: async () => {
    const response = await apiClient.get("/auth/me?include=tenant_memberships.tenant");
    return response.data;
  },

  /**
   * Initiate OAuth login
   * @param {string} provider - OAuth provider (google, github, etc.)
   * @returns {Promise<Object>} JSON:API response with authorization URL
   */
  initiateOAuth: async (provider, tenantSubdomain) => {
    const payload = { provider };
    if (tenantSubdomain) {
      payload.tenant_subdomain = tenantSubdomain;
    }
    const response = await apiClient.post("/auth/oauth/initiate/", payload);
    return response.data;
  },

  /**
   * Handle OAuth callback
   * @param {string} code - OAuth authorization code
   * @param {string} state - OAuth state parameter
   * @returns {Promise<Object>} JSON:API response with user data and token
   */
  handleOAuthCallback: async (code, state) => {
    const response = await apiClient.post("/auth/oauth/callback/", { code, state });
    return response.data;
  },

  /**
   * The host an OAuth sign-in (or Microsoft connect) started on, read from
   * its signed state and validated by the backend.
   * @param {string} state - OAuth state parameter
   * @returns {Promise<string>} e.g. "riverbend.graphit.io" or the app host
   */
  oauthReturnHost: async (state) => {
    const response = await apiClient.post("/auth/oauth/return-host/", { state });
    return response.data?.data?.attributes?.return_host;
  },

  /**
   * Logout current user
   * @returns {Promise<void>}
   */
  logout: async () => {
    await apiClient.post("/auth/logout/");
  },

  /**
   * Refresh authentication token
   * @returns {Promise<Object>} JSON:API response with token
   */
  refreshToken: async () => {
    const response = await apiClient.post("/auth/refresh/");
    return response.data;
  },

  /**
   * Update user profile information
   * @param {Object} data - Profile data to update
   * @param {string} data.first_name - User's first name
   * @param {string} data.last_name - User's last name
   * @returns {Promise<Object>} JSON:API response with updated user data
   */
  updateProfile: async (data) => {
    const response = await apiClient.patch("/auth/users/me/", data);
    return response.data;
  },

  /**
   * Upload user profile photo
   * @param {File} file - Profile photo file
   * @returns {Promise<Object>} JSON:API response with updated user data
   */
  uploadProfilePhoto: async (file) => {
    const formData = new FormData();
    formData.append('photo', file);
    const response = await apiClient.post("/auth/profile/photo/", formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  /**
   * Change user password
   * @param {Object} data - Password change data
   * @param {string} data.current_password - Current password
   * @param {string} data.new_password - New password
   * @returns {Promise<void>}
   */
  changePassword: async (data) => {
    await apiClient.post("/auth/change-password/", data);
  },

  /**
   * Signup new user with tenant creation
   * @param {Object} data - Signup data
   * @param {string} data.email - User email
   * @param {string} data.password - Password
   * @param {string} data.confirm_password - Password confirmation
   * @param {string} data.name - User's full name
   * @param {string} data.organization_name - Organization name
   * @param {string} data.subdomain - Tenant subdomain
   * @returns {Promise<Object>} Response with success message and email
   */
  signup: async (data) => {
    const response = await apiClient.post("/auth/signup/", data);
    return response.data;
  },

  /**
   * Verify email with token
   * @param {string} token - Email verification token
   * @returns {Promise<Object>} JSON:API response with user data and tenant memberships
   */
  verifyEmail: async (token) => {
    const response = await apiClient.post("/auth/verify-email/", { token });
    return response.data;
  },

  /**
   * Resend verification email
   * @param {string} email - Email address to resend verification to
   * @returns {Promise<Object>} Response with success message
   */
  resendVerificationEmail: async (email) => {
    const response = await apiClient.post("/auth/resend-verification/", { email });
    return response.data;
  },

  /**
   * Ask for a password reset link.
   *
   * Always resolves, whether or not the address belongs to an account - the
   * API answers identically either way so the form cannot be used to discover
   * which emails are registered.
   *
   * @param {string} email
   * @returns {Promise<Object>} Response carrying the generic confirmation
   */
  requestPasswordReset: async (email) => {
    const response = await apiClient.post("/auth/password/reset/request/", { email });
    return response.data;
  },

  /**
   * Check a reset token before showing the new-password form.
   *
   * Does not consume the token. Lets an expired or already-used link say so
   * up front rather than after somebody has typed a new password twice.
   *
   * The API answers 400 for a bad token with the verdict in the body, so an
   * invalid token resolves here rather than throwing - only a genuine
   * transport or server failure is left to reject.
   *
   * @param {string} token - Token from the emailed link
   * @returns {Promise<{valid: boolean, error?: string}>}
   */
  validatePasswordResetToken: async (token) => {
    try {
      const response = await apiClient.post("/auth/password/reset/validate/", { token });
      return response.data?.data?.attributes ?? { valid: false };
    } catch (error) {
      const attributes = error.response?.data?.data?.attributes;
      if (attributes && attributes.valid === false) {
        return attributes;
      }
      throw error;
    }
  },

  /**
   * Set a new password using a reset token.
   *
   * Does NOT sign the user in - the view's header docstring claims it does,
   * but PasswordResetConfirmView returns a bare message and the user has to
   * log in again. Send them to the login page, not into the app.
   *
   * @param {string} token - Token from the emailed link
   * @param {string} newPassword
   * @returns {Promise<Object>} JSON:API confirmation message
   */
  confirmPasswordReset: async (token, newPassword) => {
    const response = await apiClient.post("/auth/password/reset/confirm/", {
      token,
      new_password: newPassword,
    });
    return response.data;
  },

  /**
   * Run the backend's password rules ahead of submit, so a form can show each
   * rule met or unmet while the user types. Used by signup, reset and change.
   *
   * The "not too similar to your email or name" rule needs to know whose
   * password it is: pass `email` and `name` for an account being created, or
   * the reset `token`. A signed-in user (change password) needs neither - the
   * server uses the session.
   *
   * @param {string} password
   * @param {{email?: string, name?: string, token?: string}} [context]
   * @returns {Promise<{valid: boolean, errors: string[]}>}
   */
  checkPassword: async (password, context = {}) => {
    const response = await apiClient.post("/auth/signup/check-password/", { password, ...context });
    return response.data;
  },

  /**
   * Whether this visitor may start a new account or organization right now.
   * Sign-ups open at CITN: off the allowed network the answer is closed.
   * @returns {Promise<{open: boolean, message: string}>}
   */
  signupAvailability: async () => {
    const response = await apiClient.get("/auth/signup/availability/");
    return response.data;
  },

  /**
   * Check subdomain availability
   * @param {string} subdomain - Subdomain to check
   * @returns {Promise<Object>} Response with availability status and suggestion
   */
  checkSubdomain: async (subdomain) => {
    const response = await apiClient.get(`/auth/check-subdomain/?subdomain=${subdomain}`);
    return response.data;
  },

  /**
   * List the current user's active pending signups (reservations awaiting a plan)
   * @returns {Promise<{pending_signups: Array}>}
   */
  getPendingSignups: async () => {
    const response = await apiClient.get("/auth/signup/pending/");
    return response.data;
  },

  /**
   * Reserve a new organization for the already-authenticated user.
   * Organization details only — unlike signup(), it asks for no account fields.
   * @param {{organization_name: string, subdomain: string}} data
   * @returns {Promise<{pending_signup: {id: string, organization_name: string, subdomain: string}}>}
   */
  reserveOrganization: async (data) => {
    const response = await apiClient.post("/auth/signup/reserve-organization/", data);
    return response.data;
  },

  /**
   * Provision a free trial tenant from a pending signup
   * @param {string} pendingSignupId
   * @returns {Promise<{tenant: {id, name, subdomain}}>}
   */
  selectTrialPlan: async (pendingSignupId) => {
    const response = await apiClient.post(
      "/auth/signup/select-trial/",
      { pending_signup_id: pendingSignupId },
      { timeout: SIGNUP_PROVISIONING_TIMEOUT_MS }
    );
    return response.data;
  },

  /**
   * Create a Stripe Checkout session for a paid plan during signup
   * @param {Object} params
   * @param {string} params.pendingSignupId
   * @param {string} params.planId
   * @param {'monthly'|'yearly'} params.billingCycle
   * @returns {Promise<{checkout_url: string}>}
   */
  createSignupCheckout: async ({ pendingSignupId, planId, billingCycle }) => {
    const response = await apiClient.post("/auth/signup/checkout/", {
      pending_signup_id: pendingSignupId,
      plan_id: planId,
      billing_cycle: billingCycle,
      success_url: appAbsoluteUrl(
        `/signup/checkout-return?pending_signup_id=${pendingSignupId}&checkout=success`
      ),
      cancel_url: appAbsoluteUrl("/signup/select-plan?checkout=canceled"),
    });
    return response.data;
  },

  /**
   * Poll provisioning status for a pending signup (self-healing on the backend)
   * @param {string} pendingSignupId
   * @returns {Promise<{status: string, tenant?: Object, error_message?: string}>}
   */
  getSignupStatus: async (pendingSignupId) => {
    // The status poll self-heals a paid checkout by provisioning in-request.
    const response = await apiClient.get("/auth/signup/status/", {
      params: { pending_signup_id: pendingSignupId },
      timeout: SIGNUP_PROVISIONING_TIMEOUT_MS,
    });
    return response.data;
  },

  /**
   * Request a magic link email for passwordless login
   * @param {string} email - User email
   * @param {string} [tenantSubdomain] - Verified sign-in organization
   * @returns {Promise<Object>} Response with message and show_password_option flag
   */
  requestMagicLink: async (email, tenantSubdomain) => {
    const payload = { email };
    if (tenantSubdomain) payload.tenant_subdomain = tenantSubdomain;
    const response = await apiClient.post("/auth/magic-link/request/", payload);
    return response.data;
  },

  /**
   * Verify a magic link token and login
   * @param {string} token - Magic link token from URL
   * @returns {Promise<Object>} JSON:API response with user data and tenant memberships
   */
  verifyMagicLink: async (token) => {
    const response = await apiClient.post("/auth/magic-link/verify/", { token });
    return response.data;
  },

  /**
   * Switch to a different tenant
   * Updates the backend session's current_tenant_id
   * @param {number|string} tenantId - ID of the tenant to switch to
   * @returns {Promise<Object>} Response with tenant details and role
   */
  switchTenant: async (tenantId) => {
    const response = await apiClient.post("/auth/tenant/switch/", { tenant_id: tenantId });
    return response.data;
  },

  /**
   * End the superuser impersonation held in this session; the session is the
   * admin's own again (ClickUp 86e39gctg).
   * @returns {Promise<Object>}
   */
  exitImpersonation: async () => {
    const response = await apiClient.post("/auth/impersonate/exit/");
    return response.data;
  },

  /**
   * Begin MFA setup — returns secret and otpauth URI for QR code
   * @returns {Promise<Object>} { secret, otpauth_uri }
   */
  setupMFA: async () => {
    const response = await apiClient.post("/auth/mfa/setup/");
    return response.data;
  },

  /**
   * Verify MFA setup with a TOTP code from the authenticator app
   * @param {string} code - 6-digit TOTP code
   * @returns {Promise<Object>} { backup_codes }
   */
  verifyMFASetup: async (code) => {
    const response = await apiClient.post("/auth/mfa/verify-setup/", { code });
    return response.data;
  },

  /**
   * Disable MFA for the current user
   * @param {string} code - TOTP code to confirm disable
   * @returns {Promise<Object>}
   */
  disableMFA: async (code) => {
    const response = await apiClient.post("/auth/mfa/disable/", { code });
    return response.data;
  },

  /**
   * Get current MFA status for the authenticated user
   * @returns {Promise<Object>} { is_enabled, backup_codes_remaining }
   */
  getMFAStatus: async () => {
    const response = await apiClient.get("/auth/mfa/status/");
    return response.data;
  },

  /**
   * Complete MFA challenge during login
   * @param {string} code - 6-digit TOTP code or 8-char backup code
   * @returns {Promise<Object>} JSON:API response with user data and tenant memberships
   */
  challengeMFA: async (code) => {
    const response = await apiClient.post("/auth/mfa/challenge/", { code });
    return response.data;
  },
};

export default authAPI;
